import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  TutorQuizDTO,
  TutorQuizSummaryDTO,
  TutorResolvedConfigDTO,
  acceptQuiz,
  deleteQuiz,
  generateQuiz,
  listNodeQuizzes,
} from '../../../services/tutorQuizService';
import TutorQuizPreview from './TutorQuizPreview';

interface Props {
  courseId: number;
  nodeId: number;
  resolvedConfig: TutorResolvedConfigDTO;
}

function statusColor(status: string): 'success' | 'warning' | 'default' {
  if (status === 'ACTIVE') return 'success';
  if (status === 'DRAFT' || status === 'PENDING_REVIEW') return 'warning';
  return 'default';
}

export default function TutorNodeQuizList({ courseId, nodeId, resolvedConfig }: Props) {
  const [quizzes, setQuizzes] = useState<TutorQuizSummaryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generatedQuiz, setGeneratedQuiz] = useState<TutorQuizDTO | null>(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setQuizzes(await listNodeQuizzes(courseId, nodeId));
    } catch {
      setError('Failed to load quizzes.');
    } finally {
      setLoading(false);
    }
  }, [courseId, nodeId]);

  useEffect(() => { load(); }, [load]);

  function openGenerateDialog() {
    const types = (resolvedConfig.allowedQuestionTypes ?? 'MULTIPLE_CHOICE')
      .split(',').map((t: string) => t.trim()).filter(Boolean);
    setSelectedTypes(types);
    setGenerateDialogOpen(true);
  }

  function toggleType(type: string) {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  }

  async function handleGenerate() {
    if (selectedTypes.length === 0) return;
    setGenerating(true);
    try {
      const quiz = await generateQuiz(courseId, nodeId, selectedTypes.join(','));
      setGeneratedQuiz(quiz);
    } catch {
      setError('AI quiz generation failed. Please try again.');
    } finally {
      setGenerating(false);
      setGenerateDialogOpen(false);
    }
  }

  async function handleSaveGenerated() {
    if (!generatedQuiz?.id) return;
    await acceptQuiz(courseId, nodeId, generatedQuiz.id);
    setGeneratedQuiz(null);
    await load();
  }

  async function handleDiscard() {
    if (generatedQuiz?.id) {
      try { await deleteQuiz(courseId, nodeId, generatedQuiz.id); } catch { /* ignore */ }
    }
    setGeneratedQuiz(null);
  }

  async function handleDelete(quizId: number) {
    setDeleting(true);
    try {
      await deleteQuiz(courseId, nodeId, quizId);
      setDeleteConfirmId(null);
      await load();
    } catch {
      setError('Failed to delete quiz.');
    } finally {
      setDeleting(false);
    }
  }

  const allTypes = ['MULTIPLE_CHOICE', 'TRUE_FALSE'];
  const typeLabels: Record<string, string> = { MULTIPLE_CHOICE: 'Multiple Choice', TRUE_FALSE: 'True / False' };

  if (generatedQuiz) {
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>Review Generated Quiz</Typography>
        <TutorQuizPreview quiz={generatedQuiz} onSave={handleSaveGenerated} onDiscard={handleDiscard} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">Quizzes</Typography>
        <Button variant="outlined" size="small" onClick={openGenerateDialog}>
          Generate Quiz with AI
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
      ) : quizzes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No quizzes yet.</Typography>
      ) : (
        <Stack spacing={1}>
          {quizzes.map(quiz => (
            <Paper key={quiz.id} variant="outlined" sx={{ px: 2, py: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box flex={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={500}>{quiz.title}</Typography>
                    <Chip label={quiz.status} size="small" color={statusColor(quiz.status)} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {quiz.questionCount} questions
                    {quiz.timeLimitMinutes ? ` · ${quiz.timeLimitMinutes} min` : ''}
                    {quiz.minPassPercent ? ` · Pass ${quiz.minPassPercent}%` : ''}
                  </Typography>
                </Box>
                <Tooltip title="Delete quiz">
                  <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(quiz.id!)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Generate dialog */}
      <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate Quiz with AI</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Select question types for this generation. Changes here are not saved to config.
          </Typography>
          <FormGroup>
            {allTypes.map(type => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={selectedTypes.includes(type)}
                    onChange={() => toggleType(type)}
                    disabled={selectedTypes.length === 1 && selectedTypes.includes(type)}
                  />
                }
                label={typeLabels[type] ?? type}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)} disabled={generating}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating || selectedTypes.length === 0}
            startIcon={generating ? <CircularProgress size={16} /> : undefined}
          >
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)} maxWidth="xs">
        <DialogTitle>Delete Quiz?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This will permanently delete the quiz and all its questions.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
